import React from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../store';
import { useTranslation } from 'react-i18next';
import { Address } from '../../common/address';
import { ErrorType } from '../../../domains/common.domain';
import { ContactForm } from '../../common/contact-form';
import { CheckboxComponent } from 'n2p-ui-library';
import StringInput from '../../common/inputs/string-input';
import SelectInput from '../../common/inputs/select-input';

const BillingInformation = observer(() => {
  const { t } = useTranslation('', { keyPrefix: 'BILLING_INFORMATION' });
  const { t: tError } = useTranslation('', { keyPrefix: 'BILLING_INFORMATION.ERRORS' });
  const { billingAddressStore, quoteStore, billingInformationStore: store, billingContactsStore: contactsStore, tenantStore } = useStore();
  const {
    invoiceAnalysisContactPhone,
    invoiceAnalysisContactEmail,
    invoiceDueDay,
    stateTaxId,
    nationalTaxId,
    signatoryPosition,
    signerFirstName,
    signerLastName,
    nationalId,
  } = store.information;
  const { address } = billingAddressStore;
  const { contacts } = contactsStore;
  const {
    displayNationalTaxId,
    displayStateTaxId,
    displayInvoiceAnalysisFields,
    invoiceDueDays,
    displaySignerInformation,
    displaySignatoryPosition,
    displayNationalId,
    nationalTaxIdMaxLength,
    nationalIdMaxLength,
    maxPhoneNumberLength,
    minPhoneNumberLength,
  } = tenantStore.config;

  const updateInformation = (field: keyof typeof store.information, value: string) => {
    store.updateInformation(field, value);
  };

  const handleAddressChange = (field: keyof typeof billingAddressStore.address, value: string) => {
    store.updateAddress(field, value);
  };

  const setAddressError = (field: keyof typeof billingAddressStore.address, error: ErrorType) => {
    billingAddressStore.setError(field, error);
  };

  return (
    <div className={'step'}>
      <h2>{t('BILLING_INFORMATION')}</h2>
      <div className={'container-input company-name'}>
        <label htmlFor="cust_company_name">{t('COMPANY_NAME')}</label>
        <div className={'controls'}>
          <input type="text" name="cust_company_name" value={quoteStore.quoteInfo?.customer.name} disabled={true} />
        </div>
      </div>

      {displayNationalTaxId && (
        <StringInput
          value={nationalTaxId}
          label={t('NATIONAL_TAX_ID')}
          name={'nationalTaxId'}
          onUpdate={value => updateInformation('nationalTaxId', value)}
          error={''}
          disabled={true}
          maxLength={nationalTaxIdMaxLength}
        />
      )}

      {displayStateTaxId && (
        <StringInput
          value={stateTaxId}
          name={'stateTaxId'}
          label={t('STATE_TAX_ID')}
          onUpdate={value => updateInformation('stateTaxId', value)}
          error={stateTaxId.errorType ? tError(`${stateTaxId.errorType.toUpperCase()}_STATE_TAX_ID`) : ''}
        />
      )}

      {displayInvoiceAnalysisFields && (
        <div>
          <StringInput
            value={invoiceAnalysisContactEmail}
            label={t('INVOICE_ANALYSIS_CONTACT_EMAIL')}
            name={'invoiceAnalysisContactEmail'}
            onUpdate={value => updateInformation('invoiceAnalysisContactEmail', value)}
            error={
              invoiceAnalysisContactEmail.errorType
                ? tError(`${invoiceAnalysisContactEmail.errorType.toUpperCase()}_INVOICE_ANALYSIS_CONTACT_EMAIL`)
                : ''
            }
          />
          <StringInput
            value={invoiceAnalysisContactPhone}
            label={t('INVOICE_ANALYSIS_CONTACT_PHONE')}
            name={'invoiceAnalysisContactPhone'}
            onUpdate={value => updateInformation('invoiceAnalysisContactPhone', value)}
            maxLength={maxPhoneNumberLength}
            error={
              invoiceAnalysisContactPhone.errorType
                ? tError(`${invoiceAnalysisContactPhone.errorType.toUpperCase()}_INVOICE_ANALYSIS_CONTACT_PHONE`, {
                    data: minPhoneNumberLength,
                  })
                : ''
            }
          />
        </div>
      )}

      {!!invoiceDueDays && (
        <SelectInput
          value={invoiceDueDay}
          label={t('INVOICE_DUE_DAY')}
          error={invoiceDueDay.errorType ? tError(`${invoiceDueDay.errorType.toUpperCase()}_INVOICE_DUE_DAY`) : ''}
          selectLabel={t('SELECT')}
          onUpdate={value => updateInformation('invoiceDueDay', value)}
          selectValues={invoiceDueDays.split(',').map(day => ({ label: t('DAY_OF_THE_MONTH', { day }), value: day }))}
        />
      )}

      {displaySignerInformation && (
        <div>
          <h3>{t('SIGNATORY_INFORATION')}</h3>
          <span className="step-info">{t('PLEASE_ENTER_THE_DATA_OF_THE_PERSON')}</span>

          <div className="container-two-columns">
            <StringInput
              value={signerFirstName}
              label={t('FIRST_NAME')}
              name={'signerFirstName'}
              onUpdate={value => updateInformation('signerFirstName', value)}
              error={signerFirstName.errorType ? tError(`${signerFirstName.errorType.toUpperCase()}_FIRST_NAME`) : ''}
            />
            <StringInput
              value={signerLastName}
              label={t('LAST_NAME')}
              name={'signerLastName'}
              onUpdate={value => updateInformation('signerLastName', value)}
              error={signerLastName.errorType ? tError(`${signerLastName.errorType.toUpperCase()}_LAST_NAME`) : ''}
            />
          </div>

          <div className="container-two-columns">
            {displaySignatoryPosition && (
              <StringInput
                value={signatoryPosition}
                label={t('SIGNATORY_POSITION')}
                name={'signatoryPosition'}
                onUpdate={value => updateInformation('signatoryPosition', value)}
                error={signatoryPosition.errorType ? tError(`${signatoryPosition.errorType.toUpperCase()}_SIGNATORY_POSITION`) : ''}
              />
            )}
            {displayNationalId && (
              <StringInput
                value={nationalId}
                label={t('NATIONAL_ID')}
                name={'nationalId'}
                onUpdate={value => updateInformation('nationalId', value)}
                error={
                  nationalId.errorType ? tError(`${nationalId.errorType.toUpperCase()}_NATIONAL_ID`, { data: nationalIdMaxLength }) : ''
                }
                maxLength={nationalIdMaxLength}
              />
            )}
          </div>
        </div>
      )}

      <h3>{t('BILLING_ADDRESS')}</h3>
      <span className={'step-info'}>{t('ADDRESS_WILL_APPEAR_ON_YOUR_INVOICE')}</span>
      {(quoteStore.isServiceStepAvailable || quoteStore.isShippingStepAvailable) && (
        <div className={'radio-group'}>
          <div className={'radio-wrapper'}>
            <label htmlFor={'new-address'} className={'n2pc-radio'}>
              <input
                id={'new-address'}
                name="addressType"
                type="radio"
                onChange={() => (store.addressType = 'billing')}
                checked={store.addressType === 'billing'}
              />
              <span className={'n2pc-radio-mask'}></span>
              <span>{t('NEW_ADDRESS')}</span>
            </label>
          </div>
          {quoteStore.isServiceStepAvailable && (
            <div className={'radio-wrapper'}>
              <label htmlFor={'service-address'} className={'n2pc-radio'}>
                <input
                  id={'service-address'}
                  name="addressType"
                  type="radio"
                  onChange={() => (store.addressType = 'service')}
                  checked={store.addressType === 'service'}
                />
                <span className={'n2pc-radio-mask'}></span>
                <span>{t('SAME_AS_SERVICE_ADDRESS')}</span>
              </label>
            </div>
          )}
          {quoteStore.isShippingStepAvailable && (
            <div className={'radio-wrapper'}>
              <label htmlFor={'shipping-address'} className={'n2pc-radio'}>
                <input
                  id={'shipping-address'}
                  name="addressType"
                  type="radio"
                  onChange={() => (store.addressType = 'shipping')}
                  checked={store.addressType === 'shipping'}
                />
                <span className={'n2pc-radio-mask'}></span>
                <span>{t('SAME_AS_SHIPPING_ADDRESS')}</span>
              </label>
            </div>
          )}
        </div>
      )}
      <Address
        address={address}
        handleChange={handleAddressChange}
        setError={setAddressError}
        selectedCountry={billingAddressStore.selectedCountry}
        onCityNormalized={city => billingAddressStore.updateAddress('city', city)}
      />

      <h3>{t('BILLING_CONTACTS')}</h3>
      <span className={'step-info'}>{t('WHO_SHOULD_BE_GIVEN_ACCESS')}</span>
      {!quoteStore.isContactOnlyPartner && (
        <div className={'container-same-as'}>
          <CheckboxComponent
            label={t('SAME_AS_FIRST_ONBOARDING_CONTACT')}
            onChange={e => store.updateSameAsOnboardingContacts()}
            checked={store.isSameAsOnboardingContacts}
          />
        </div>
      )}
      {contacts.map((contact, index) => (
        <ContactForm
          key={contact.uiKey}
          index={index}
          contact={contact}
          handleChange={(field, value) => store.updateContact(index, field, value)}
          removeContact={() => store.removeContact(index)}
          contactsType={'billing'}
        />
      ))}

      {contacts.length < 3 && (
        <button className={'n2pc-btn-text contact-add-btn'} onClick={() => store.addContact()}>
          {t('ADD_CONTACT')}
        </button>
      )}
    </div>
  );
});

export default BillingInformation;
